import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { BoltIcon, PencilIcon, ChartBarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ProgramsManager from '../Programs/ProgramsManager';
import { PROGRAM_TYPES } from '../../services/programsService';
import { firestore, auth } from '../../database/firebase';
import { collection, getCountFromServer, doc, getDoc, setDoc } from 'firebase/firestore';

const MODULES = [
  {
    id: 1,
    title: 'Acceleration Strategy & Scale Readiness Check',
    description:
      'Assess your business readiness for scaling and develop a comprehensive acceleration strategy.',
    duration: '3 hours',
    participants: 28,
    rating: 4.7,
    type: 'interactive',
    category: 'Strategy',
    difficulty: 'Intermediate',
    prerequisites: 'Business Fundamentals',
    lastUpdated: '2024-01-15',
  },
  {
    id: 2,
    title: 'Lead Generation Engine',
    description:
      'Build a systematic approach to generating qualified leads for your business growth.',
    duration: '4 hours',
    participants: 35,
    rating: 4.8,
    type: 'video',
    category: 'Marketing',
    difficulty: 'Intermediate',
    prerequisites: 'Acceleration Strategy',
    lastUpdated: '2024-01-18',
  },
  {
    id: 3,
    title: 'Lead Nurturing & Conversion Mastery',
    description: 'Master the art of nurturing leads and converting them into loyal customers.',
    duration: '3.5 hours',
    participants: 32,
    rating: 4.6,
    type: 'interactive',
    category: 'Sales',
    difficulty: 'Advanced',
    prerequisites: 'Lead Generation Engine',
    lastUpdated: '2024-01-20',
  },
  {
    id: 4,
    title: 'Customer Profiling & Retargeting',
    description:
      'Create detailed customer profiles and implement effective retargeting strategies.',
    duration: '2.5 hours',
    participants: 29,
    rating: 4.5,
    type: 'document',
    category: 'Analytics',
    difficulty: 'Intermediate',
    prerequisites: 'Lead Nurturing',
    lastUpdated: '2024-01-22',
  },
  {
    id: 5,
    title: 'Increasing Average Transaction Value (ATV)',
    description:
      'Strategies to increase the value of each customer transaction and maximize revenue.',
    duration: '3 hours',
    participants: 31,
    rating: 4.7,
    type: 'video',
    category: 'Revenue',
    difficulty: 'Advanced',
    prerequisites: 'Customer Profiling',
    lastUpdated: '2024-01-25',
  },
  {
    id: 6,
    title: 'Boosting Frequency of Purchase',
    description: 'Techniques to encourage repeat purchases and increase customer lifetime value.',
    duration: '2.5 hours',
    participants: 27,
    rating: 4.4,
    type: 'interactive',
    category: 'Retention',
    difficulty: 'Intermediate',
    prerequisites: 'ATV Strategies',
    lastUpdated: '2024-01-28',
  },
];

const defaultLessons = [
  {
    id: 1,
    title: 'Scaling Readiness Assessment',
    description: 'Evaluate your business readiness for scaling with a structured assessment.',
    type: 'interactive',
    duration: '45 minutes',
    difficulty: 'Intermediate',
    rating: 4.7,
    started: false,
    completed: false,
    content: 'Covers key factors for scale readiness and strategic planning.',
    resources: [
      { title: 'Readiness Checklist', url: '#' },
      { title: 'Strategy Template', url: '#' },
    ],
  },
  {
    id: 2,
    title: 'Lead Generation Fundamentals',
    description: 'Learn the basics of building a lead generation engine.',
    type: 'video',
    duration: '60 minutes',
    difficulty: 'Intermediate',
    rating: 4.8,
    started: false,
    completed: false,
    content: 'Covers lead generation strategies and tools for scaling.',
    resources: [{ title: 'Lead Gen Guide', url: '#' }],
  },
];

const defaultSubmissions = [
  {
    id: 1,
    title: 'Scaling Strategy Submission',
    studentName: 'Alex Smith',
    type: 'document',
    status: 'pending',
    submittedAt: '2024-06-21',
    description: 'Submission of scaling strategy and readiness assessment.',
    content: 'My scaling strategy focuses on lead generation and operational efficiency...',
    rating: null,
    feedback: null,
  },
];

const initialStats = {
  totalModules: MODULES.length,
  completedModules: 0,
  totalLessons: defaultLessons.length,
  completedLessons: 0,
  averageRating: 4.7,
  totalParticipants: 38,
  activeParticipants: 38,
};

const tabs = [
  { id: 'overview', name: 'Course Overview', icon: BoltIcon },
  { id: 'modules', name: 'Training Modules', icon: DocumentTextIcon },
  { id: 'lessons', name: 'Lesson Tracker', icon: ChartBarIcon },
  { id: 'submissions', name: 'Submissions', icon: PencilIcon },
];

const ScalingModules = ({ userId: propUserId, role: propRole, userRole: overrideUserRole }) => {
  const userId = propUserId || auth?.currentUser?.uid || null;
  const userRole = propRole || 'client';

  const effectiveUserRole = overrideUserRole || userRole;

  const [participantsCount, setParticipantsCount] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const programKey = 'SCALING_MODULES';

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const collections = ['clients', 'coaches', 'moderator', 'admin', 'alumni'];
        const results = await Promise.all(
          collections.map(name => getCountFromServer(collection(firestore, name)))
        );
        const total = results.reduce((sum, snap) => sum + (snap.data().count || 0), 0);
        setParticipantsCount(total);
      } catch (e) {
        toast.error('Failed to count participants');
        throw e;
      }
    };
    loadCounts();
  }, []);

  useEffect(() => {
    const loadRating = async () => {
      try {
        if (!userId) return;
        const ratingRef = doc(firestore, 'programRatings', `${userId}_${programKey}`);
        const snap = await getDoc(ratingRef);
        if (snap.exists()) {
          const r = Number(snap.data()?.rating || 0);
          setUserRating(isNaN(r) ? 0 : r);
        }
      } catch (e) {
        toast.error('Failed to load rating');
        throw e;
      }
    };
    loadRating();
  }, [userId]);

  const handleRate = async value => {
    try {
      if (!userId) {
        toast.error('Please sign in to rate.');
        throw new Error('Please sign in to rate.');
      }
      setUserRating(value);
      const ratingRef = doc(firestore, 'programRatings', `${userId}_${programKey}`);
      await setDoc(
        ratingRef,
        { userId, program: programKey, rating: value, updatedAt: new Date() },
        { merge: true }
      );
      toast.success('Thanks for your rating!');
    } catch (e) {
      toast.error('Failed to save rating');
      throw e;
    }
  };

  return (
    <div className="space-y-4">
      <ProgramsManager userRole={effectiveUserRole} programType={PROGRAM_TYPES.SCALING_MODULES} />
    </div>
  );
};

export default ScalingModules;
